import { useEffect } from 'react'
import { LogoMark } from '@/components/brand/LogoMark'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getAuthenticatedUserProfile } from '@/services/api'
import { isAdminEligible } from '@/types/api'
import { getActingAs, clearActingAs } from '@/state/actingAs'

interface AdminRouteProps {
  children: React.ReactNode
}

/**
 * Gates /admin to root and partner organizations. Assumes it's nested under
 * ProtectedRoute, so the caller is already authenticated by this point.
 *
 * Also drops any act-as state on the way in: administering another org while
 * acting as a third one doesn't make sense, and it's what leaves the admin
 * shortcut and the rest of the product showing your real identity's admin
 * powers in the middle of impersonating someone who wouldn't have them.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  useEffect(() => {
    if (getActingAs()) clearActingAs()
  }, [])

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getAuthenticatedUserProfile,
  })

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <LogoMark size={48} className="mx-auto mb-4 animate-spin [animation-duration:1.6s]" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAdminEligible(profile?.user.organization)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
